import { AlertTriangle, ArrowLeft, Check, FileSpreadsheet, FileText, GitCompareArrows, ShieldCheck } from 'lucide-react'
import type { CandidateChange, ImportRow, ProjectRow } from '../types'
import { HelpTooltip } from '../components/HelpTooltip'

type DifferencesViewProps = {
  project: ProjectRow
  importRow: ImportRow | null
  busy: boolean
  onBack: () => void
  onToggle: (candidateId: string) => void
  onAcceptAll: () => void
  onApply: () => void
}

function valueLabel(value: string | number | boolean | null) {
  if (value === null || value === '') return 'Vide'
  if (value === true) return 'Oui'
  if (value === false) return 'Non'
  return String(value)
}

function CandidateRow({ candidate, formatLabel, onToggle }: { candidate: CandidateChange; formatLabel: string; onToggle: () => void }) {
  return (
    <article className={`difference-row ${candidate.conflict ? 'conflict' : ''}`}>
      <button className={`decision-check ${candidate.accepted ? 'accepted' : ''}`} onClick={onToggle} type="button" aria-label={candidate.accepted ? 'Refuser ce changement' : 'Accepter ce changement'}>
        {candidate.accepted ? <Check size={17} /> : null}
      </button>
      <div className="difference-content">
        <header><strong>{candidate.label}</strong>{candidate.conflict ? <span title={`Suivi Aziz a changé depuis la création de ce ${formatLabel}. Comparez les trois valeurs avant de décider.`}><AlertTriangle size={14} /> Conflit avec une modification récente</span> : null}</header>
        <div className="three-way-diff">
          <div><span title={`Valeur conservée dans la copie exacte ayant servi à créer le ${formatLabel}`}>Au moment de l’export</span><strong>{valueLabel(candidate.before)}</strong></div>
          <div className="returned"><span title="Valeur lue dans le fichier que vous venez de soumettre">Dans le {formatLabel} retourné</span><strong>{valueLabel(candidate.returned)}</strong></div>
          <div><span title="Valeur actuellement enregistrée dans le projet">Dans Suivi Aziz maintenant</span><strong>{valueLabel(candidate.current)}</strong></div>
        </div>
      </div>
    </article>
  )
}

export function DifferencesView({ project, importRow, busy, onBack, onToggle, onAcceptAll, onApply }: DifferencesViewProps) {
  if (!importRow) {
    return (
      <main className="workspace empty-differences">
        <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> Retour au projet</button>
        <FileSpreadsheet size={42} />
        <h1>Aucune différence en attente</h1>
        <p>Soumettez un Excel ou un Word précédemment téléchargé depuis ce projet, ou revenez lorsqu’une nouvelle référence est disponible.</p>
      </main>
    )
  }

  const accepted = importRow.candidates.filter((candidate) => candidate.accepted).length
  const formatLabel = importRow.format === 'docx' ? 'Word' : 'Excel'
  const FormatIcon = importRow.format === 'docx' ? FileText : FileSpreadsheet
  return (
    <main className="workspace differences-view">
      <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> Retour au projet</button>
      <div className="page-heading compact">
        <div><span className="eyebrow">RÉCONCILIATION {formatLabel.toUpperCase()}</span><h1>Vérifier les différences</h1><p><FormatIcon size={15} /> {project.name} · {importRow.filename}</p></div>
        <div className="difference-count"><GitCompareArrows size={20} /><strong>{importRow.candidates.length}</strong><span>changement{importRow.candidates.length > 1 ? 's' : ''} proposé{importRow.candidates.length > 1 ? 's' : ''}</span></div>
      </div>

      <section className="reconciliation-principle">
        <ShieldCheck size={23} />
        <div><strong>Vous gardez la main. <HelpTooltip text="Chaque proposition compare trois valeurs : celle de l’export, celle du fichier retourné et celle actuellement enregistrée. Vous choisissez ce qui doit être appliqué." label={`Comment fonctionne la comparaison ${formatLabel} ?`} align="left" /></strong><span>Nous comparons la base exportée, le {formatLabel} retourné et la vérité actuelle. Rien n’est appliqué sans votre décision.</span></div>
      </section>

      <div className="difference-actions">
        <span>{accepted} changement{accepted > 1 ? 's' : ''} sélectionné{accepted > 1 ? 's' : ''}</span>
        <button className="text-button" onClick={onAcceptAll} title="Sélectionner toutes les propositions visibles sans encore les appliquer" type="button">Tout sélectionner</button>
      </div>

      <section className="difference-list">
        {importRow.candidates.length ? importRow.candidates.map((candidate) => <CandidateRow key={candidate.id} candidate={candidate} formatLabel={formatLabel} onToggle={() => onToggle(candidate.id)} />) : (
          <div className="no-change"><Check size={24} /><strong>Aucune différence métier détectée.</strong><span>Le fichier peut être archivé sans modifier le projet.</span></div>
        )}
      </section>

      <footer className="reconciliation-footer">
        <button className="secondary-button" onClick={onBack} type="button">Annuler</button>
        <button className="primary-button" onClick={onApply} disabled={busy || accepted === 0} title="Enregistrer uniquement les changements sélectionnés et les ajouter à l’historique" type="button">Appliquer {accepted || ''} changement{accepted > 1 ? 's' : ''}</button>
      </footer>
    </main>
  )
}
